import { m } from 'framer-motion';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Typography, Grid, Stack, Card, alpha, CardContent, CardActions, IconButton, Hidden } from '@mui/material';
// components
import Slider from 'react-slick';
import { useRef } from 'react';
import { MotionViewport, varFade } from '../../components/animate';
import { CarouselArrows, CarouselDots } from '../../components/carousel';
import Iconify from '../../components/Iconify';
import TextMaxLine from '../../components/TextMaxLine';
import useActualites from '../../hooks/useActualites';
import { sortActualitesByDate } from '../../utils/sortActualites';

// ----------------------------------------------------------------------
const LATEST = [
  {
    image: '/assets/background/rapport_eco.jpeg',
    comment: 0,
    title: 'Le Rapport économique « YEARBOOK TOGO 2022-2023 » est officiel...',
    desc: 'Le Rapport économique « YEARBOOK TOGO 2022-2023 » est officiellement lancé ce mardi 30 août 2022 lors d’une cér...',
  },
  {
    image: '/assets/background/zener.jpeg',
    comment: 0,
    title: "ZENER S.A. obtient un financement pour  l'expansion de son activi...",
    desc: 'Célébrons nos champions nationaux ! Dans le cadre de son programme d’investissement, l’entreprise nationale   ZEN...',
  },
  {
    image: '/assets/background/card1.jpg',
    comment: 0,
    title: 'Attraction d’IDE : Une délégation d’Investisseurs turcs pro...',
    desc: 'Le lundi, 23 mai 2022, le Ministère de la Promotion de l’Investissement a accueilli, en présence de l’Ambassadrice...',
  },
  {
    image: '/assets/background/card2.jpg',
    comment: 0,
    title: 'La loi de finances 2022 : une part belle pour les projets sociaux...',
    desc: 'L’Assemblée Nationale a adopté, le mardi 28 décembre 2021, le budget de l’Etat exercice 2022, qui s’élève à....',
  },
  {
    image: '/assets/background/card3.jpg',
    comment: 0,
    title: 'Les Rencontres Africa à Lomé : retour sur un forum qui a tenu s...  ',
    desc: 'Le Togo a abrité du 30 novembre au 02 décembre 2021 à l’hôtel 2 Février, le forum international : « les Rencontr...',
  },
];

const CardStyle = styled(Card)(({ theme }) => {
  const shadowCard = (opacity) =>
    theme.palette.mode === 'light'
      ? alpha(theme.palette.grey[500], opacity)
      : alpha(theme.palette.common.black, opacity);

  return {
    border: 0,
    maxWidth: 380,
    minWidth: 340,
    minHeight: 500,
    maxHeight: 500,
    margin: 'auto',
    marginTop: 50,
    textAlign: 'center',
    boxShadow: theme.customShadows.z12,
    [theme.breakpoints.up('md')]: {
      boxShadow: 'none',
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    '&.cardCenter': {
      [theme.breakpoints.up('md')]: {
        paddingLeft: 50,
        paddingRight: 50,
        backgroundColor: theme.palette.background.paper,
        '&:before': {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          content: "''",
          margin: 'auto',
          position: 'absolute',
          width: 'calc(100% - 40px)',
          height: 'calc(100% - 40px)',
          borderRadius: Number(theme.shape.borderRadius) * 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `-20px 20px 40px 0 ${shadowCard(0.12)}`,
        },
      },
    },
  };
});

const CardImageStyle = styled(m.img)({
  width: '100%',
  height: 230,
  objectFit: 'cover',
});

const getCardDescription = (card) =>
  String(card.description || card.desc || '')
    .replace(/<img[^>]*>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// ----------------------------------------------------------------------

export default function HomeLastNews() {
  const slider = useRef(null);
  const { actualites } = useActualites();
  const latestNews = sortActualitesByDate(actualites).slice(0, 6);
  const cards = latestNews.length ? latestNews : LATEST;

  const settings = {
    dots: true,
    arrows: false,
    centerMode: false,
    swipeToSlide: true,
    focusOnSelect: true,
    variableWidth: true,
    centerPadding: '0px',
    slidesToShow: 2,
    ...CarouselDots(),
  };

  const handlePrevious = () => {
    slider.current?.slickPrev();
  };

  const handleNext = () => {
    slider.current?.slickNext();
  };

  return (
    <Stack width={1} component={MotionViewport} pl={{ xs: 2, md: 7 }} pb={5} pt={5}>
      <Grid container alignItems="center" justifyContent="space-between" spacing={{ xs: 1, md: 3 }}>
        <Grid item xs={12} md={2} sx={{ textAlign: { xs: 'center', sm: 'center', md: 'left' } }}>
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} width={1} justifyContent="center">
            <m.div variants={varFade().inDown}>
              <Typography variant="h3">Nos</Typography>
            </m.div>
            <m.div variants={varFade().inDown}>
              <Typography variant="h3">dernières</Typography>
            </m.div>
            <m.div variants={varFade().inDown}>
              <Typography variant="h3">nouvelles</Typography>
            </m.div>
          </Stack>
          <Stack width={1} alignItems={{ xs: 'center', md: 'start' }}>
            <m.div variants={varFade().inDown}>
              <Box height={7} width={100} bgcolor="#F9D141" mt={1} />
            </m.div>
          </Stack>
        </Grid>

        <Grid item xs={12} md={10}>
          <Hidden mdDown>
            <Stack
              display={{ xs: 'none', md: 'flex' }}
              width={1}
              px={1}
              pb={1}
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Stack>
                <CarouselArrows color="#cb011c" filled onNext={handleNext} onPrevious={handlePrevious} />
              </Stack>
            </Stack>

            <Stack position="relative" height={640}>
              <Box
                component={m.div}
                variants={varFade().inRight}
                width={0.8}
                bgcolor="#aed7bd"
                top={0}
                right={0}
                bottom={0}
                position="absolute"
                borderRadius="50px 0 0 50px"
              />
              <Box width={1} position="absolute" top={0} bottom={0} left={0} right={0}>
                <Slider ref={slider} {...settings}>
                  {cards.map((card) => (
                    <Stack p={3} component={m.div} variants={varFade().inUp} key={card.id || card.title}>
                      <CardStyle>
                        <Stack height={490} justifyContent="space-between">
                          <CardImageStyle src={card.image} alt={card.title} />

                          <CardContent>
                            <Stack justifyContent="space-between">
                              <Typography color="#156C53" gutterBottom variant="h5" component="div" textAlign="left">
                                {card.title}
                              </Typography>
                              <TextMaxLine line={3} variant="body2" color="text.secondary" textAlign="left">
                                {getCardDescription(card)}
                              </TextMaxLine>
                            </Stack>
                          </CardContent>

                          <CardActions>
                            <Stack width={1} direction="row" justifyContent="end" px={2} pt={1}>
                              <IconButton>
                                <Iconify icon="eva:more-horizontal-fill" color="red" />
                              </IconButton>
                            </Stack>
                          </CardActions>
                        </Stack>
                      </CardStyle>
                    </Stack>
                  ))}
                </Slider>
              </Box>
            </Stack>
          </Hidden>
          <Hidden mdUp>
            <Stack width={1} spacing={2} justifyContent="center">
              {cards.map((card) => (
                <Stack p={{ md: 3 }} component={m.div} variants={varFade().inUp} key={card.id || card.title}>
                  <CardStyle>
                    <Stack height={490} justifyContent="space-between">
                      <CardImageStyle src={card.image} alt={card.title} />

                      <CardContent>
                        <Stack justifyContent="space-between">
                          <Typography color="#156C53" gutterBottom variant="h5" component="div" textAlign="left">
                            {card.title}
                          </Typography>
                          <TextMaxLine line={3} variant="body2" color="text.secondary" textAlign="left">
                            {getCardDescription(card)}
                          </TextMaxLine>
                        </Stack>
                      </CardContent>

                      <CardActions>
                        <Stack width={1} direction="row" justifyContent="end" px={2} pt={1}>
                          <IconButton>
                            <Iconify icon="eva:more-horizontal-fill" color="red" />
                          </IconButton>
                        </Stack>
                      </CardActions>
                    </Stack>
                  </CardStyle>
                </Stack>
              ))}
            </Stack>
          </Hidden>
        </Grid>
      </Grid>
    </Stack>
  );
}
